import ServiceStore from "./ServiceStore";
import { OfflineServiceStore } from "./OfflineServiceStore";
import type RootStore from "./RootStore";
import type { ServicePath } from "../types/ServiceStoreTypeMap";

export interface ServiceStoreFactoryOptions {
  enableOffline?: boolean;
  conflictStrategy?: "client" | "server" | "merge" | "manual";
  cacheTTL?: number;
  syncOnReconnect?: boolean;
  [key: string]: unknown;
}

/**
 * Factory to create ServiceStore instances with optional offline capabilities
 * This allows gradual migration of existing stores to offline-first architecture
 *
 * @example
 * // Typed store creation - full type inference
 * const customersStore = ServiceStoreFactory.create(rootStore, 'customers');
 * const customers = customersStore.entities; // Type: customer[]
 *
 * // Untyped store creation - falls back to BaseEntity
 * const customStore = ServiceStoreFactory.create(rootStore, 'custom-service');
 */
export class ServiceStoreFactory {
  /**
   * Stateless services that should NOT have offline enabled.
   * These are calculators, validators, and other services that don't persist data.
   */
  private static readonly disabledOfflineStores = [
    "manhole-calculator",
    "manhole-quote-calculator",
    "manhole-validations",
    "manhole-quote-converter",
    "manhole-pdf",
    "wa-tax-lookup",
  ];

  /**
   * Create a typed ServiceStore for known service paths
   */
  static create<S extends ServicePath>(
    rootStore: RootStore,
    serviceName: S,
    options?: ServiceStoreFactoryOptions
  ): ServiceStore<S>;

  /**
   * Create a ServiceStore for unknown service paths (fallback)
   */
  static create(
    rootStore: RootStore,
    serviceName: string,
    options?: ServiceStoreFactoryOptions
  ): ServiceStore<string>;

  /**
   * Implementation
   */
  static create<S extends string>(
    rootStore: RootStore,
    serviceName: S,
    options: ServiceStoreFactoryOptions = {}
  ): ServiceStore<S> {
    // Check if offline is explicitly enabled/disabled for this store
    // Default to true (offline-first) unless explicitly disabled
    const enableOffline = options.enableOffline ?? true;

    // Check if this store should have offline enabled
    // Disable for stateless services (calculators, validators, etc.)
    // or if feature flag is explicitly set to disable offline
    const shouldEnableOffline =
      enableOffline &&
      !this.disabledOfflineStores.includes(serviceName) &&
      !this.isFeatureFlagDisabled("offline_all_stores");

    if (shouldEnableOffline) {
      // Create offline-capable store
      // Disable parent's enableStorage since OfflineServiceStore manages its own storage
      // and shares the reference with the parent class to avoid duplicate writes
      const offlineOptions = {
        ...options,
        enableStorage: false, // Prevent ServiceStore from creating duplicate storage
        offline: {
          enableOffline: true,
          conflictStrategy: options.conflictStrategy || "server",
          cacheTTL: options.cacheTTL || 5 * 60 * 1000, // 5 minutes default
          syncOnReconnect: options.syncOnReconnect ?? true,
        },
      };

      return new OfflineServiceStore<S>(
        rootStore,
        serviceName,
        offlineOptions
      ) as ServiceStore<S>;
    } else {
      // Create standard store
      return new ServiceStore<S>(
        rootStore,
        serviceName,
        options as Record<string, unknown>
      );
    }
  }

  /**
   * Check if a feature flag is enabled
   * This can be extended to check localStorage, environment variables, or remote config
   */
  private static isFeatureFlagEnabled(flag: string): boolean {
    // Check localStorage for feature flags
    try {
      const flags = localStorage.getItem("featureFlags");
      if (flags) {
        const parsedFlags = JSON.parse(flags);
        return parsedFlags[flag] === true;
      }
    } catch (e) {
      // Ignore errors
    }

    // Check environment variables
    if (typeof process !== "undefined" && process.env) {
      return process.env[`VITE_FEATURE_${flag.toUpperCase()}`] === "true";
    }

    return false;
  }

  /**
   * Check if a feature flag is explicitly disabled
   * This allows for rollback via feature flag
   */
  private static isFeatureFlagDisabled(flag: string): boolean {
    // Check localStorage for feature flags
    try {
      const flags = localStorage.getItem("featureFlags");
      if (flags) {
        const parsedFlags = JSON.parse(flags);
        return parsedFlags[flag] === false;
      }
    } catch (e) {
      // Ignore errors
    }

    // Check environment variables
    if (typeof process !== "undefined" && process.env) {
      return process.env[`VITE_FEATURE_${flag.toUpperCase()}`] === "false";
    }

    return false;
  }

  /**
   * Enable offline mode for a specific store type globally
   */
  static enableOfflineForStore(storeName: string) {
    try {
      const flags = localStorage.getItem("offlineStores") || "[]";
      const offlineStores = JSON.parse(flags);
      if (!offlineStores.includes(storeName)) {
        offlineStores.push(storeName);
        localStorage.setItem("offlineStores", JSON.stringify(offlineStores));
      }
    } catch (e) {
      console.error("Failed to enable offline for store:", e);
    }
  }

  /**
   * Disable offline mode for a specific store type globally
   */
  static disableOfflineForStore(storeName: string) {
    try {
      const flags = localStorage.getItem("offlineStores") || "[]";
      const offlineStores = JSON.parse(flags);
      const index = offlineStores.indexOf(storeName);
      if (index > -1) {
        offlineStores.splice(index, 1);
        localStorage.setItem("offlineStores", JSON.stringify(offlineStores));
      }
    } catch (e) {
      console.error("Failed to disable offline for store:", e);
    }
  }

  /**
   * Get list of stores with offline enabled
   */
  static getOfflineStores(): string[] {
    try {
      const flags = localStorage.getItem("offlineStores") || "[]";
      return JSON.parse(flags);
    } catch (e) {
      return [];
    }
  }
}
